<template>
	<div class="enable-multi-factor social-login__form">
		<Callout v-if="!useVerifyFlow || enableStep !== 2" type="info" :size="useVerifyFlow ? 's' : 'm'">
			<p>{{ organization }} requires two-factor authentication.</p>
		</Callout>

		<!-- New verify flow -->
		<template v-if="useVerifyFlow">
			<!-- Step 1: Backup email entry -->
			<template v-if="enableStep === 1">
				<ValidationObserver
					ref="backupEmailObserver"
					v-slot="{ passed, validate }"
					tag="form"
					novalidate
					class="cog-form"
					@submit.prevent="beginBackupEmailVerification()"
				>
					<ValidatedInput
						id="backup-email-address"
						v-model.trim="backupEmailAddress"
						vid="backup-email-address"
						type="email"
						name="Backup Email Address"
						placeholder="Backup Email Address"
						autocomplete="on"
						rules="required|email|is-not-same-email"
						class="mt-4 mb-2"
						@input="validate()"
					/>
					<div class="helptext text-1 mb-2">This backup email will be used to send authentication codes when you cannot access your authenticator app.</div>
					<div class="dialog__footer flex flex--sb flex--end">
						<StatusButton class="button button--primary text-4 my-3" :status="status" :disabled="!passed" type="submit">
							Next
						</StatusButton>
					</div>
				</ValidationObserver>
			</template>

			<!-- Step 2: Backup email verification code -->
			<template v-else-if="enableStep === 2">
				<Callout type="info" class="mt-4">
					<p class="m-0">We sent a 6-digit code to <strong>{{ backupEmailAddress }}</strong>.</p>
				</Callout>
				<label class="label mt-4">Verification code</label>
				<MultifactorInput :is-validated="isBackupCodeValidated" :is-loading="isValidatingBackupCode" :label="'Check your email for a verification code'" @complete="verifyBackupCode" />
				<div class="dialog__footer flex flex--sb flex--end">
					<StatusButton class="button button--secondary text-4 my-3" :status="resendStatus" @click="resendBackupCode()">Resend code</StatusButton>
				</div>
			</template>

			<!-- Step 3: TOTP setup -->
			<template v-else-if="enableStep === 3">
				<div class="enable-multi-factor__container mb-4">
					<MultifactorSecret v-if="secret" :secret="secret" :setup-url="setupUrl" />
					<MultifactorInput :is-validated="isCodeValidated" :is-loading="isValidatingInput" @complete="validateCode" />
				</div>
				<div class="dialog__footer flex flex--sb flex--end">
					<StatusButton class="button button--primary text-4 my-3" :status="status" :disabled="!isCodeValidated" :auto-reset="false" @click="handleEnableMfa">
						Enable
					</StatusButton>
				</div>
			</template>
		</template>

		<!-- Legacy flow -->
		<template v-else>
			<div class="enable-multi-factor__container mb-4">
				<MultifactorSecret v-if="secret" :secret="secret" :setup-url="setupUrl" />
				<MultifactorInput :is-validated="isCodeValidated" :is-loading="isValidatingInput" @complete="validateCode" />
			</div>
			<ValidationObserver
				ref="observer"
				tag="form"
				novalidate
				class="cog-form"
				data-vv-scope="observer"
				@submit.prevent="handleEnableMfa"
			>
				<ValidatedInput
					id="email-address"
					ref="email"
					v-model.trim="backupEmailAddress"
					vid="email-address"
					type="email"
					name="Backup Email Address"
					placeholder="Backup Email Address"
					autocomplete="on"
					rules="required|email|is-not-same-email"
					class="mt-4 mb-2"
				/>
				<div class="helptext text-1 mb-2">This backup email will be used to send authentication codes when you cannot access your authenticator app.</div>
				<div class="dialog__footer flex flex--sb flex--end">
					<StatusButton class="button button--primary text-4 my-3" :status="status" type="submit">
						Enable
					</StatusButton>
				</div>
			</ValidationObserver>
		</template>
	</div>
</template>

<script lang="ts">
	import MultifactorSecret from 'src/components/form-controls/MultifactorSecret.vue';
	import MultifactorInput from 'src/components/form-controls/MultifactorInput.vue';
	import { validateTempOtp } from '@cognitoforms/api/services/settings/account-service';
	import { enableMultifactor, initEnableMultifactor, sendAuthBackupEmailVerification, verifyAuthBackupEmailCode } from '@cognitoforms/api/services/auth-service';
	import { ValidationObserver, extend } from 'vee-validate';
	import type { ButtonStatus } from 'src/components/interaction/StatusButton.vue';
	import StatusButton from 'src/components/interaction/StatusButton.vue';
	import ValidatedInput from 'src/components/form-controls/ValidatedInput.vue';
	import Callout from 'src/components/content/Callout.vue';
	import notify from 'src/utilities/notify';
	import { computed, defineComponent, ref, watch } from 'vue';
	import { CognitoConfiguration } from '@cognitoforms/utils/clientside-configuration';
	import { useRoute } from 'vue-router/composables';

	export default defineComponent({
		components: {
			MultifactorSecret,
			MultifactorInput,
			ValidationObserver,
			StatusButton,
			ValidatedInput,
			Callout
		},
		props: {
			rememberMe: { type: Boolean, default: false },
			encryptedAuthMethod: { type: String, default: null }
		},
		emits: ['title-changed'],
		setup(props, { emit }) {
			const secret = ref<string>('');
			const setupUrl = ref<string>('');
			const isCodeValidated = ref<boolean>(false);
			const isLoading = ref<boolean>(false);
			const isValidatingInput = ref<boolean>(false);

			const emailAddress = ref<string>(null);
			const otp = ref<string>('');

			const organization = ref<string>('');

			const hasError = ref<boolean>(false);
			const backupEmailAddress = ref<string>('');
			watch(backupEmailAddress, (newEmail) => {
				if (newEmail)
					hasError.value = false;
			});

			const useVerifyFlow = ref<boolean>(false);

			const enableStep = ref<number>(1);
			const isBackupCodeValidated = ref<boolean>(false);
			const isValidatingBackupCode = ref<boolean>(false);
			const resendStatus = ref<ButtonStatus>('default');

			const status = computed<ButtonStatus>(() => {
				if (isLoading.value)
					return 'validating';
				else if (!isCodeValidated.value || !backupEmailAddress.value || emailAddress.value === backupEmailAddress.value)
					return 'disabled';
				else if (hasError.value)
					return 'error';
				return 'default';
			});

			const validateCode = (code: string) => {
				isValidatingInput.value = true;
				validateTempOtp(secret.value, code).then(res => {
					isValidatingInput.value = false;
					isCodeValidated.value = res;
					otp.value = code;
				});
			};

			const route = useRoute();
			const observer = ref<typeof ValidationObserver>(null);
			const backupEmailObserver = ref<typeof ValidationObserver>(null);

			const beginBackupEmailVerification = () => {
				isLoading.value = true;
				sendAuthBackupEmailVerification(backupEmailAddress.value).then(() => {
					isLoading.value = false;
					enableStep.value = 2;
				}).catch(err => {
					isLoading.value = false;
					if (err.statusCode === 400)
						backupEmailObserver.value?.setErrors({ 'backup-email-address': err.message });
					else
						notify('An error has occurred', 'error');
				});
			};

			const verifyBackupCode = (code: string) => {
				isValidatingBackupCode.value = true;
				verifyAuthBackupEmailCode(backupEmailAddress.value, code).then(() => {
					isValidatingBackupCode.value = false;
					isBackupCodeValidated.value = true;
					enableStep.value = 3;
				}).catch(err => {
					isValidatingBackupCode.value = false;
					isBackupCodeValidated.value = false;
					if (err.statusCode === 400)
						notify(err.message || 'Invalid verification code', 'error');
					else
						notify('An error has occurred', 'error');
				});
			};

			const resendBackupCode = () => {
				resendStatus.value = 'validating';
				sendAuthBackupEmailVerification(backupEmailAddress.value).then(() => {
					resendStatus.value = 'default';
					notify('Verification code resent.', 'success');
				}).catch(() => {
					resendStatus.value = 'default';
					notify('An error has occurred', 'error');
				});
			};

			const handleEnableMfa = () => {
				const handleEnableError = (err) => {
					if (err.statusCode === 400) {
						if (err.message.toLowerCase().includes('email')) {
							observer.value.setErrors({ 'email-address': err.message });
							hasError.value = true;
						}
						else if (err.message.toLowerCase() === 'session is null.')
							window.location.assign('/login');
					}
					else
						notify('An error has occurred', 'error');
				};

				isLoading.value = true;
				enableMultifactor(backupEmailAddress.value, secret.value, otp.value, route.query.organizationCode as string).then(res => {
					isLoading.value = false;
					// Honor a server-supplied redirect first, and fallback to the return url in the query
					window.location.assign(res?.RedirectUrl || (route.query.returnUrl as string));
				}).catch(err => {
					isLoading.value = false;
					handleEnableError(err);
				});
			};

			extend('is-not-same-email', {
				validate: value => {
					return emailAddress.value !== value;
				},
				message: 'Your backup email address must be different from your account email address.'
			});

			emit('title-changed', 'Two-Factor Authentication');
			organization.value = route.query.desiredOrganization as string;

			initEnableMultifactor().then(res => {
				secret.value = res.Secret;
				setupUrl.value = res.SetupUrl;
				emailAddress.value = res.UserEmail;
				useVerifyFlow.value = res.EnhancedMfaSecurity;
				if (res.MfaBackupEmailVerified) {
					backupEmailAddress.value = res.MfaBackupEmail;
					enableStep.value = 3;
				}
			});

			return {
				secret,
				setupUrl,
				backupEmailAddress,
				isCodeValidated,
				isLoading,
				isValidatingInput,
				otp,
				organization,
				hasError,
				status,
				useVerifyFlow,
				enableStep,
				isBackupCodeValidated,
				isValidatingBackupCode,
				resendStatus,
				validateCode,
				beginBackupEmailVerification,
				verifyBackupCode,
				resendBackupCode,
				handleEnableMfa,
				observer,
				backupEmailObserver,
				CognitoConfiguration
			};
		}
	});
</script>

<style lang="scss">
.enable-multi-factor {

	&__container {
		border: 1px solid var(--border-primary);
		padding: 1rem 1.75rem;
	}
}

[data-auth-style='app'] .social-login {

	h1 {
		font-family: var(--font-display-light);
		font-size: var(--text-7);
		font-weight: var(--font-light);
	}

	.faux-modal__body {
		@include contain-until($b5);
		padding: $space__5;
		padding-bottom: 50px;
	}

	.faux-modal__wrapper {
		max-width: 320px;
	}

	.el-input__inner {
		min-height: 40px;
		padding-left: 1em;
	}

	.button--primary {
		width: 100%;
	}

	.cog-label {
		margin-bottom: 0;
		font-size: $size__3;
		font-weight: $weight__text--normal;
	}
}
</style>
